import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Mic, MicOff, PhoneOff, X, Square } from "lucide-react";
import { CoachOrb } from "./CoachOrb";
import { API, auth, C, SERIF, SANS, InlineReset, DeeperCard, TalkingPortrait, unlockAudio, getAudioCtx } from "./CoachBits";
import { MicHelp, MicCheck, useMicReady } from "./MicHelp";

const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";

const newRec = (stream) => { const m = pickMime(); return m ? new MediaRecorder(stream, { mimeType: m }) : new MediaRecorder(stream); };

// Hands-free mic: starts recording when you speak, stops after a pause, hands back the clip.
// Uses the page's shared audio engine (unlocked by the tap that opened the call) — iPhone Safari
// keeps a freshly made one silent. If no sound arrives at all, it falls back to tap-to-talk.
export const useHandsFree = (active, onUtterance, { pauseTicks = 20, maxMs = 45000 } = {}) => {
  const [phase, setPhase] = useState("off");
  const [error, setError] = useState("");
  const [manual, setManual] = useState(false);
  const [manualRec, setManualRec] = useState(false);
  const [level, setLevel] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const micFail = (e) => setError(e?.name === "NotFoundError" || e?.name === "OverconstrainedError" ? "nomic" : "blocked");
  const stream = useRef(null);
  const mrec = useRef(null);
  const cb = useRef(onUtterance);
  cb.current = onUtterance;

  const closed = useRef(false);
  const getStream = () => {
    if (!stream.current) {
      stream.current = navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
        .then((st) => { if (closed.current) st.getTracks().forEach((t) => t.stop()); return st; })
        .catch((e) => { stream.current = null; throw e; });
    }
    return stream.current;
  };
  useEffect(() => {
    getStream().catch(micFail);
    return () => {
      closed.current = true;
      if (mrec.current) { mrec.current.onstop = null; mrec.current.stop(); mrec.current = null; }
      stream.current?.then((st) => st.getTracks().forEach((t) => t.stop())).catch(() => {});
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active || manual) { setPhase("off"); setLevel(0); return undefined; }
    let alive = true; let timer; let rec = null; let chunks = []; let src = null;
    let loud = 0; let quiet = 0; let floor = 0.006; let started = 0; let dead = 0; let ticks = 0;
    const stopRec = (keep) => {
      const r = rec; rec = null;
      if (!r) return;
      r.onstop = () => { if (keep && alive) cb.current(new Blob(chunks, { type: r.mimeType || "audio/webm" })); chunks = []; };
      r.stop();
    };
    (async () => {
      let st;
      try { st = await getStream(); } catch (e) { micFail(e); return; }
      const ctx = getAudioCtx();
      if (!alive) return;
      if (!ctx) { setManual(true); return; }
      if (ctx.state !== "running") ctx.resume().catch(() => {});
      const an = ctx.createAnalyser();
      an.fftSize = 1024;
      src = ctx.createMediaStreamSource(st);
      src.connect(an);
      const buf = new Float32Array(1024);
      setPhase("listening");
      timer = setInterval(() => {
        if (ctx.state !== "running") { setPhase("blocked"); ctx.resume().catch(() => {}); dead += 1; if (dead > 60) setManual(true); return; }
        an.getFloatTimeDomainData(buf);
        let s = 0;
        for (let i = 0; i < buf.length; i += 1) s += buf[i] * buf[i];
        const rms = Math.sqrt(s / buf.length);
        dead = rms === 0 ? dead + 1 : 0;
        if (dead > 80) { setManual(true); return; }
        ticks += 1;
        if (ticks % 2 === 0) setLevel(Math.min(1, rms * 12));
        const thresh = Math.max(0.012, floor * 2.5);
        if (!rec) {
          setPhase("listening");
          floor = floor * 0.97 + Math.min(rms, 0.04) * 0.03;
          loud = rms > thresh ? loud + 1 : 0;
          if (loud >= 2) {
            rec = newRec(st); chunks = [];
            rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
            rec.start(250);
            started = Date.now(); quiet = 0;
            setPhase("hearing");
          }
        } else {
          quiet = rms > thresh * 0.6 ? 0 : quiet + 1;
          const dur = Date.now() - started;
          if ((quiet >= pauseTicks && dur > 700) || dur > maxMs) { clearInterval(timer); stopRec(dur > 700); setPhase("off"); }
        }
      }, 50);
    })();
    return () => { alive = false; clearInterval(timer); stopRec(false); src?.disconnect(); };
  }, [active, manual, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const tapToTalk = async () => {
    if (mrec.current) { mrec.current.stop(); return; }
    let st;
    try { st = await getStream(); } catch (e) { micFail(e); return; }
    const r = newRec(st); const chunks = [];
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = () => { mrec.current = null; setManualRec(false); if (!closed.current) cb.current(new Blob(chunks, { type: r.mimeType || "audio/webm" })); };
    r.start(250); mrec.current = r; setManualRec(true);
  };

  const retry = () => { stream.current = null; closed.current = false; getStream().then(() => { setError(""); setAttempt((n) => n + 1); }).catch(micFail); };
  return { phase, error, manual, manualRec, level, tapToTalk, retry, useManual: () => setManual(true) };
};

export const NOISE = new Set(["you", "thank you", "thanks", "thanks for watching", "bye", "okay", "uh", "um", "hmm"]);

export const CoachCall = ({ who, audioEl, env, speaking, busy, guiding, msgs, opener, needTap, onTapStart, onSend, onSpeak, onPrime, onStartReset, onOpenDeeper, onClose }) => {
  const [muted, setMuted] = useState(false);
  const [orb, setOrb] = useState(false);
  const [hearing, setHearing] = useState(false);
  const resetPlaying = guiding;
  const [note, setNote] = useState("");
  const last = [...msgs].reverse().find((m) => m.role !== "user");
  const tool = last?.tool && !last.tool.done ? last.tool : null;
  const active = !muted && !speaking && !busy && !hearing && !resetPlaying;

  const onUtterance = async (blob) => {
    setHearing(true); setNote("");
    try {
      const form = new FormData();
      form.append("file", blob, `speech.${blob.type.includes("mp4") ? "mp4" : "webm"}`);
      const r = await axios.post(`${API}/api/audio-journeys/ai/transcribe`, form, auth());
      const t = (r.data?.text || "").trim();
      if (t.length > 1 && !NOISE.has(t.toLowerCase().replace(/[^a-z ]/g, "").trim())) { setHearing(false); await onSend(t); return; }
      setNote("I didn't quite catch that — say it again?");
    } catch { setNote("I couldn't hear that clearly — say it again?"); }
    setHearing(false);
  };
  const [micReady, markMic] = useMicReady();
  const mic = useHandsFree(active && micReady, onUtterance);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    setStuck(false);
    if (mic.phase !== "listening") return undefined;
    const id = setTimeout(() => setStuck(true), 12000);
    return () => clearTimeout(id);
  }, [mic.phase]);
  const manualOn = mic.manual && !speaking && !busy && !hearing && !resetPlaying;

  useEffect(() => {
    onPrime();
    if (!msgs.length && opener) onSpeak(opener);
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", esc); document.body.style.overflow = ""; audioEl?.pause(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const status = !micReady ? "Quick mic check" : mic.error ? "Microphone needed" : speaking ? "Speaking" : busy || hearing ? "Thinking" : resetPlaying ? "Breathing with you"
    : mic.manual ? (mic.manualRec ? "Hearing you · tap when done" : "Tap the mic to talk")
    : muted ? "Paused · tap the mic" : mic.phase === "blocked" ? "Tap the screen so I can hear you"
    : mic.phase === "hearing" ? "Hearing you" : "Listening · just talk";
  const hint = mic.error || !micReady ? "" : note || (mic.manual ? (manualOn ? (mic.manualRec ? "Tap the mic again when you've finished." : "Tap the mic, talk, then tap again.") : "")
    : active ? "Just talk — I'll know when you're done." : "");

  return (
    <div className="fixed inset-0 z-[90] overflow-hidden" style={{ background: C.bg, animation: "coachCallIn 380ms cubic-bezier(.2,.8,.2,1)" }} data-testid="coach-call" onPointerDown={() => { unlockAudio(); onPrime(); }}>
      <style>{"@keyframes coachCallIn{from{opacity:0;transform:scale(.96)}to{opacity:1;transform:scale(1)}}@keyframes coachPulse{0%,100%{opacity:.35;transform:scale(1)}50%{opacity:1;transform:scale(1.25)}}"}</style>
      {orb ? <CoachOrb audioEl={audioEl} env={env} speaking={speaking} micLevel={mic.phase === "hearing" ? mic.level : 0} /> : (
        <>
          <img src={who.image} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-110 hidden sm:block" style={{ filter: "blur(40px) brightness(.45)" }} />
          <TalkingPortrait who={who} audioEl={audioEl} env={env} speaking={speaking} full
            status={busy || hearing ? "thinking" : active && !speaking ? "listening" : "idle"} />
        </>
      )}
      <div className="absolute inset-x-0 top-0 h-40 pointer-events-none" style={{ background: "linear-gradient(rgba(4,10,8,.92) 35%, transparent)" }} />
      <div className="absolute inset-x-0 bottom-0 h-[55%] pointer-events-none" style={{ background: "linear-gradient(transparent, rgba(4,10,8,.92) 65%)" }} />

      <button type="button" onClick={onClose} aria-label="Close" data-testid="coach-call-close" className="absolute left-5 top-5 w-10 h-10 rounded-full inline-flex items-center justify-center"
        style={{ background: "rgba(4,10,8,.45)", backdropFilter: "blur(12px)", color: C.cream }}><X size={18} /></button>
      <div className="absolute inset-x-0 top-6 text-center pointer-events-none">
        <div className="text-3xl" style={{ fontFamily: SERIF, color: C.cream }} data-testid="coach-call-name">{who.name}</div>
        <div className="mt-1 inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.3em]" style={{ color: C.gold, fontFamily: SANS }} data-testid="coach-call-status">
          <span className="w-1.5 h-1.5 rounded-full transition-transform duration-100" data-testid="coach-call-level"
            style={{ background: C.gold, transform: `scale(${1 + mic.level * 2.5})`, animation: !mic.level && (active || speaking) ? "coachPulse 1.4s ease-in-out infinite" : "none" }} />{status}
        </div>
      </div>

      {needTap && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <button type="button" onClick={onTapStart} data-testid="coach-call-tap-start"
            className="pointer-events-auto rounded-full px-6 py-4 inline-flex items-center gap-2 text-sm shadow-2xl transition-transform duration-200 active:scale-95"
            style={{ background: C.gold, color: "#0b1430", fontFamily: SANS }}>
            <Mic size={16} /> Tap once to hear {who.name}
          </button>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 px-5 pb-8 max-w-xl mx-auto">
        {tool && (
          <div className="flex justify-center [&>*]:w-full" data-testid="coach-call-tool">
            {tool.kind === "reset" && <InlineReset tool={tool} coachName={who.name} onStart={() => { onPrime(); onStartReset(tool); }} />}
            {tool.kind === "open" && <DeeperCard tool={tool} onOpen={() => onOpenDeeper(tool)} />}
          </div>
        )}
        {!micReady && <div className="mt-4"><MicCheck name={who.name} onReady={markMic} onType={onClose} gold={C.gold} cream={C.cream} /></div>}
        {mic.error && <div className="mt-4"><MicHelp kind={mic.error} onRetry={mic.retry} onType={onClose} gold={C.gold} cream={C.cream} /></div>}
        <p className="mt-4 text-center text-sm min-h-[1.25rem]" style={{ color: note ? "#e9a39c" : C.mist, fontFamily: SANS }} data-testid="coach-call-hint">
          {hint}
        </p>
        {stuck && active && !mic.manual && (
          <button type="button" onClick={mic.useManual} className="mt-1 block mx-auto text-xs underline" style={{ color: C.dim, fontFamily: SANS }} data-testid="coach-call-use-tap">
            Not hearing you? Tap to talk instead
          </button>
        )}
        <div className="mt-5 flex items-center justify-center gap-10">
          {mic.manual ? (
            <button type="button" onClick={mic.tapToTalk} disabled={!manualOn} aria-label={mic.manualRec ? "Done talking" : "Tap to talk"} data-testid="coach-call-tap-talk"
              className="w-16 h-16 rounded-full inline-flex items-center justify-center transition-transform duration-200 active:scale-95 disabled:opacity-40"
              style={{ background: mic.manualRec ? "#c0584f" : C.gold, color: mic.manualRec ? "#fff" : C.bg }}>
              {mic.manualRec ? <Square size={18} fill="currentColor" /> : <Mic size={22} />}
            </button>
          ) : (
            <button type="button" onClick={() => setMuted((m) => !m)} aria-label={muted ? "Unmute" : "Mute"} data-testid="coach-call-mute"
              className="w-16 h-16 rounded-full inline-flex items-center justify-center transition-transform duration-200 active:scale-95"
              style={{ background: muted ? C.cream : "rgba(241,236,221,.14)", color: muted ? C.bg : C.cream, backdropFilter: "blur(12px)" }}>
              {muted ? <MicOff size={22} /> : <Mic size={22} />}
            </button>
          )}
          <button type="button" onClick={onClose} aria-label="End" data-testid="coach-call-end"
            className="w-16 h-16 rounded-full inline-flex items-center justify-center transition-transform duration-200 active:scale-95" style={{ background: "#c0453b", color: "#fff" }}>
            <PhoneOff size={22} />
          </button>
        </div>
        <button type="button" onClick={() => setOrb((o) => !o)} data-testid="coach-call-orb-toggle"
          className="mt-5 block mx-auto text-xs tracking-wide transition-opacity duration-200 hover:opacity-100 opacity-80" style={{ color: C.cream, fontFamily: SANS }}>
          {orb ? `Switch to ${who.name}` : "Switch to the orb"}
        </button>
      </div>
    </div>
  );
};
